<template>
  <div>
    <section class="relative isolate overflow-hidden py-24 sm:py-32">
      <shape-field />

      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="grid grid-cols-1 items-center gap-x-16 gap-y-16 lg:grid-cols-2">
          <div
            data-animate-group
          >
            <atomic-eyebrow :label="testimonial.sector" />

            <h1 class="mt-4 text-balance text-5xl font-display font-bold tracking-tight text-gray-900 sm:text-6xl">
              {{ testimonial.company }}
            </h1>

            <p class="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
              <span class="font-semibold text-gray-900">{{ testimonial.person.name }}</span>
              <span aria-hidden="true">·</span>
              <span>{{ testimonial.person.role }}</span>
            </p>

            <p class="mt-2 flex items-center gap-x-1.5 text-sm text-gray-600">
              <icon name="ph:map-pin-duotone" aria-hidden="true" class="size-4 flex-none text-brand-700" />
              {{ testimonial.location }}
            </p>

            <p
              data-animate="fade-up"
              class="mt-8 text-pretty text-xl/8 font-medium text-gray-900"
            >
              {{ testimonial.headline }}
            </p>

            <div
              data-animate="fade-up"
              class="mt-8 flex items-center gap-x-4"
            >
              <a
                v-if="testimonial.logoUrl"
                :href="testimonial.websiteUrl"
                target="_blank"
                rel="noopener"
                class="transition hover:opacity-80"
              >
                <img
                  :src="testimonial.logoUrl"
                  :alt="`Logo ${testimonial.company}`"
                  class="h-9 w-auto"
                />
              </a>

              <span
                v-if="testimonial.logoUrl"
                aria-hidden="true"
                class="h-8 w-px bg-gray-200"
              />

              <a
                :href="testimonial.websiteUrl"
                target="_blank"
                rel="noopener"
                class="inline-flex items-center gap-x-1.5 text-sm font-semibold text-brand-600 bg-gradient-to-r from-current to-current bg-left-bottom bg-no-repeat bg-[length:0%_1px] transition-[background-size,color] duration-200 ease-out hover:bg-[length:100%_1px] hover:text-brand-700 active:text-brand-700 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
              >
                {{ testimonial.websiteLabel }}
                <icon name="ph:arrow-up-right" aria-hidden="true" class="size-4" />
              </a>
            </div>
          </div>

          <!-- No reveal animation and eager loading: LCP candidate above the fold. -->
          <div class="relative">
            <div class="relative aspect-square overflow-hidden rounded-3xl bg-white ring-1 ring-gray-200 shadow-soft">
              <testimonial-media
                :media="testimonial.heroMedia"
                is-priority
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="relative isolate overflow-hidden py-24 sm:py-32">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <atomic-section-heading
          align="center"
          kicker="Le point de départ"
          :title="`Pourquoi ${testimonial.company} a changé de solution`"
          description="Les outils utilisés auparavant, leurs limites, et ce qui a déclenché le changement."
          class="mx-auto"
        />

        <div class="mt-16 grid grid-cols-1 gap-x-16 gap-y-12 lg:grid-cols-2">
          <div
            data-animate="fade-right"
            class="flex flex-col"
          >
            <p class="text-xs font-semibold uppercase tracking-wider text-gray-500">Le blocage</p>
            <p class="mt-4 flex-1 text-lg/8 text-gray-600">
              {{ testimonial.context.before }}
            </p>

            <figure
              v-if="testimonial.context.quote"
              class="mt-6 border-l-4 border-gray-200 pl-6"
            >
              <blockquote class="text-lg/8 font-medium text-gray-900">
                « {{ testimonial.context.quote.text }} »
              </blockquote>
              <figcaption class="mt-3 text-sm text-gray-600">
                <span class="font-semibold text-gray-900">{{ testimonial.context.quote.author }}</span>
                — {{ testimonial.context.quote.role }}
              </figcaption>
            </figure>
          </div>

          <div
            data-animate="fade-left"
            class="flex flex-col"
          >
            <p class="text-xs font-semibold uppercase tracking-wider text-brand-700">Le déclic</p>
            <p class="mt-4 flex-1 text-lg/8 text-gray-600">
              {{ testimonial.context.trigger }}
            </p>

            <figure
              v-if="testimonial.declicQuote"
              class="mt-6 border-l-4 border-brand-200 pl-6"
            >
              <blockquote class="text-lg/8 font-medium text-gray-900">
                « {{ testimonial.declicQuote.text }} »
              </blockquote>
              <figcaption class="mt-3 text-sm text-gray-600">
                <span class="font-semibold text-gray-900">{{ testimonial.declicQuote.author }}</span>
                — {{ testimonial.declicQuote.role }}
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>

    <section class="relative isolate overflow-hidden py-24 sm:py-32">
      <shape-field origin="right" />

      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <atomic-section-heading
          align="center"
          kicker="Résultats concrets"
          :title="`Les écrans de ${testimonial.company} aujourd'hui`"
          :description="testimonial.impactSummary"
          class="mx-auto"
        />

        <div
          data-animate-group
          class="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2"
        >
          <div
            v-if="mediaAt(1)"
            data-animate="zoom"
            class="relative aspect-video overflow-hidden rounded-3xl bg-white ring-1 ring-gray-200 shadow-soft"
          >
            <testimonial-media :media="mediaAt(1)!" />
          </div>
          <div
            v-if="mediaAt(0)"
            data-animate="zoom"
            class="relative aspect-video overflow-hidden rounded-3xl bg-white ring-1 ring-gray-200 shadow-soft"
          >
            <testimonial-media :media="mediaAt(0)!" />
          </div>
        </div>

        <dl
          data-animate-group
          class="mt-16 grid grid-cols-1 divide-y divide-gray-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0"
        >
          <div
            v-for="stat in testimonial.stats"
            :key="stat.title"
            data-animate="fade-up"
            class="py-6 sm:px-6 sm:py-0 sm:first:pl-0 sm:last:pr-0"
          >
            <dt class="text-balance text-xl font-display font-bold tracking-tight text-gray-900">
              {{ stat.title }}
            </dt>
            <dd class="mt-2 text-sm/6 text-gray-600">
              {{ stat.description }}
            </dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="relative isolate overflow-hidden pb-24 sm:pb-32">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <figure
          data-animate="fade-up"
          class="mx-auto max-w-3xl text-center"
        >
          <icon name="ph:quotes-fill" aria-hidden="true" class="mx-auto size-10 text-brand-300" />
          <blockquote class="mt-6 text-balance text-3xl sm:text-4xl leading-tight font-display font-medium text-gray-900">
            « {{ testimonial.quote.text }} »
          </blockquote>
          <figcaption class="mt-8">
            <div class="font-display font-semibold text-gray-900">{{ testimonial.quote.author }}</div>
            <div class="text-sm text-gray-600">{{ testimonial.quote.role }}</div>
          </figcaption>
        </figure>
      </div>
    </section>

    <section-cta
      title="Envie du même résultat sur vos écrans&nbsp;?"
      description="Demandez une démo gratuite et sans engagement. Nous vous accompagnons pour configurer vos premiers écrans en moins d'une heure."
      primary-label="Demander une démo"
      primary-link="/demo"
      :secondary-label="testimonial.relatedLabel ?? 'Voir les cas d\'usages'"
      :secondary-link="testimonial.relatedLink ?? '/cas-usages'"
    />

    <section-related-links :links="relatedLinks" />
  </div>
</template>

<script setup lang="ts">
import type { Testimonial, TestimonialMedia } from 'types/testimonial'

const route = useRoute()
const slug = String(route.params.slug)
const found = getTestimonialBySlug(slug)

if (!found) {
  throw createError({ statusCode: 404, statusMessage: 'Témoignage introuvable', fatal: true })
}

const testimonial: Testimonial = found

function mediaAt (index: number): TestimonialMedia | undefined {
  return testimonial.media?.[index]
}

const config = useRuntimeConfig()
const env = config.public
const baseUrl = env.PUBLIC_URL.replace(/\/+$/, '')

const relatedLinks = getCrossLinks(['casUsages', 'fonctionnalites', 'ecrans'])

const ogImage = testimonial.heroMedia.type === 'image' ? testimonial.heroMedia.src : undefined

useSeo({
  title: testimonial.seoTitle ?? `Témoignage ${testimonial.company} — ${appName}`,
  description: testimonial.seoDescription ?? testimonial.intro,
  path: `/temoignages/${testimonial.slug}`,
  ogType: 'article',
  ogImage,
  jsonLd: [
    getBreadcrumbJsonLd(baseUrl, [
      { name: 'Accueil', path: '/' },
      { name: 'Témoignages', path: '/temoignages' },
      { name: testimonial.company, path: `/temoignages/${testimonial.slug}` }
    ]),
    ...getTestimonialVideoJsonLd(baseUrl, testimonial)
  ]
})
</script>
